// react
import { MouseEvent, MouseEventHandler, useState, useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// hooks
import { useAuth } from '../../../hooks/useAuth';
import { useFetch } from '../../../hooks/useFetch';

// sse
import { useSSE } from '../../../utils/sse/useSSE';

// mui
import {
	AppBar,
	Avatar,
	Badge,
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	InputAdornment,
	Link,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
} from '@mui/material';

// types
import { Activity } from '../../../@types';

// context
import { ContextUserEntity } from '../../../App.context';

// ui
import { LineItemProps } from '../LineItem/LineItem';
import { Notifications } from './Notifications/Notifications';

// links
import { LinkLearning } from '../../links/LinkLearning/LinkLearning';

// fields
import { SelectUserEntity } from '../../fields/SelectUserEntity/SelectUserEntity';

// icons
import {
	CircleOutlined as IconCircle,
	MenuOutlined as IconMenu,
	NotificationsOutlined as IconNotifications,
	SearchOutlined as IconSearch,
} from '@mui/icons-material';

// img
import { LogoFundsChainAlt } from '../../img/LogoFundsChainAlt';

// utils
import { constructActivityItems, paramPopulateActivities } from '../../../utils/activities';
import { translateRole } from '../../../utils/translations';

// theme
import { theme } from '../../../theme';

// props
export interface HeaderProps {
	onOpenNav?: MouseEventHandler;
}

export type Anchor = HTMLButtonElement | null;

export const Header = ({ onOpenNav }: HeaderProps) => {
	// hooks
	const { logout } = useAuth();
	const { t } = useTranslation();

	// context
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [activities, setActivities] = useState<Activity[]>([]);

	// vars
	const sessionUser = sessionStorage.getItem('user');
	const user = sessionUser ? JSON.parse(sessionUser) : null;

	// SSE connection
	const {
		isConnected: isSseConnected,
		messageCount: sseMessageCount,
		notifications: sseNotifications,
		status: sseStatus,
	} = useSSE({
		onNotification: (notification) => {
			console.log('[SSE] Notification received:', notification);
		},
	});

	// SSE status color
	const sseStatusColor =
		sseStatus === 'connected'
			? 'success.main'
			: sseStatus === 'connecting'
			? 'warning.main'
			: sseStatus === 'error'
			? 'error.main'
			: 'grey.500';

	const sseStatusLabel =
		sseStatus === 'connected'
			? `${t('connected')} (${sseMessageCount} ${t('messages')})`
			: sseStatus === 'connecting'
			? t('connecting')
			: sseStatus === 'error'
			? t('error')
			: t('disconnected');

	let urlFetchActivities = `${process.env.REACT_APP_API_URL}/activities?type=!PAYMENT_CREATED&populate=${paramPopulateActivities}&limit=10&orderBy=created`;

	if (user?.abac?.projects?.length) {
		urlFetchActivities += `&project=${user.abac.projects.join(',')}`;
	}

	// fetch activities
	useFetch({
		isEnabled: Boolean(user),
		url: urlFetchActivities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActivities(res.data);
			}
		},
	});

	// items
	const activityItems: LineItemProps[] = constructActivityItems({
		activities,
		t,
	});

	// state
	const [anchorElAccount, setAnchorElAccount] = useState<Anchor>(null);
	const [anchorElNotifications, setAnchorElNotifications] = useState<Anchor>(null);
	const [anchorElSse, setAnchorElSse] = useState<Anchor>(null);

	let userChipLabel = null;

	if (user) {
		userChipLabel = translateRole(user.role, t);
	}

	const handleOpenAccount = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElAccount(e.currentTarget);
	};

	const handleOpenNotifications = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElNotifications(e.currentTarget);
	};

	const handleOpenSse = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElSse(e.currentTarget);
	};

	const handleCloseAccount = () => {
		setAnchorElAccount(null);
	};

	const handleCloseNotifications = () => {
		setAnchorElNotifications(null);
	};

	const handleCloseSse = () => {
		setAnchorElSse(null);
	};

	const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElAccount(null);
		logout();
	};

	let backgroundColor = theme.palette.brand.blue[800];
	let envName = '';

	if (process.env.REACT_APP_ENV) {
		if (['development', 'local', 'qa'].includes(process.env.REACT_APP_ENV)) {
			backgroundColor = theme.palette.brand.blue[700];
		}

		if (process.env.REACT_APP_ENV === 'development') {
			envName = t('env.dev');
		} else if (process.env.REACT_APP_ENV === 'qa') {
			envName = t('env.qa');
		} else if (process.env.REACT_APP_ENV === 'local') {
			envName = t('env.local');
		}
	}

	return (
		<AppBar
			elevation={0}
			position="static"
			sx={{
				backgroundColor,
				alignItems: 'center',
				borderRadius: '0 !important',
				flexDirection: 'row',
				height: theme.variables?.headerHeight,
			}}>
			<Toolbar
				disableGutters={true}
				sx={{
					height: '100%',
					justifyContent: 'space-between',
					minHeight: '0 !important',
					padding: theme.spacing(0, 2),
					width: '100%',
				}}>
				<Grid
					alignItems="center"
					columnSpacing={2}
					container
					sx={{ justifyContent: { xs: 'space-between', lg: 'flex-end' } }}>
					<Grid item xs={4} md={3} lg={5} sx={{ display: { lg: 'none' } }}>
						<Box
							sx={{
								alignItems: 'center',
								color: 'common.white',
								display: 'flex',
								gap: 1,
								textDecoration: 'none',
							}}>
							<RouterLink to="/" style={{ display: 'flex', textDecoration: 'none' }}>
								<Box sx={{ width: 150 }}>
									<LogoFundsChainAlt />
								</Box>
							</RouterLink>
						</Box>
					</Grid>

					<Grid item xs={8} md={9} lg={7}>
						<Grid
							alignItems="center"
							container
							columnSpacing={{ xs: 1, md: 2 }}
							justifyContent="flex-end">
							<Grid hidden item xs="auto" md={true}>
								<TextField
									color="white"
									fullWidth={true}
									sx={{ display: { xs: 'none !important', md: 'inline-flex !important' } }}
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										startAdornment: (
											<InputAdornment position="start">
												<IconSearch />
											</InputAdornment>
										),
									}}
									placeholder={t('search')}
									size="small"
									variant="filled"
								/>
							</Grid>

							<Grid item xs="auto" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
								<Grid alignItems="center" container spacing={2}>
									<Grid item xs="auto">
										<Grid alignItems="center" container spacing={1}>
											<Grid item>
												<Chip color="primary" label={t('new')} size="small" variant="filled" />
											</Grid>
											<Grid item>
												<Link
													color="common.white"
													component={RouterLink}
													to={`/features/r380`}
													underline="hover">
													{t('r380')}
												</Link>
											</Grid>
										</Grid>
									</Grid>
									<Grid
										item
										xs="auto"
										sx={{ display: { xs: 'none', md: 'none', lg: 'inline-flex' } }}>
										<LinkLearning />
									</Grid>
								</Grid>
							</Grid>

							{envName && (
								<Grid item xs="auto">
									<Chip color="error" label={envName} size="small" />
								</Grid>
							)}
							{process.env.REACT_APP_SSE_URL && (
								<Grid item xs="auto">
									<div>
										<Tooltip title={`SSE: ${sseStatusLabel}`}>
											<IconButton
												color="white"
												onClick={handleOpenSse}
												size="small"
												sx={{ p: 0.5 }}>
												<Badge
													badgeContent={sseNotifications.length}
													color="primary"
													max={99}
													invisible={sseNotifications.length === 0}>
													<IconCircle
														sx={{
															color: sseStatusColor,
															fontSize: 16,
															animation:
																sseStatus === 'connecting' ? 'pulse 1.5s infinite' : 'none',
															'@keyframes pulse': {
																'0%': { opacity: 1 },
																'50%': { opacity: 0.4 },
																'100%': { opacity: 1 },
															},
														}}
													/>
												</Badge>
											</IconButton>
										</Tooltip>

										<Popover
											anchorEl={anchorElSse}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'center',
											}}
											onClose={handleCloseSse}
											open={Boolean(anchorElSse)}>
											<Box sx={{ width: 360, maxHeight: 400 }}>
												<Box
													sx={{
														p: 2,
														borderBottom: 1,
														borderColor: 'divider',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
													}}>
													<Typography variant="subtitle2">
														{t('sse.messages')} ({sseNotifications.length})
													</Typography>
													<Chip
														color={isSseConnected ? 'success' : 'default'}
														label={sseStatusLabel}
														size="small"
													/>
												</Box>
												<Box sx={{ maxHeight: 320, overflow: 'auto' }}>
													{sseNotifications.length === 0 ? (
														<Box sx={{ p: 2, textAlign: 'center' }}>
															<Typography color="text.secondary" variant="body2">
																{t('sse.noMessages')}
															</Typography>
														</Box>
													) : (
														sseNotifications.map((notification, index) => (
															<Box
																key={notification.id || index}
																sx={{
																	p: 2,
																	borderBottom: 1,
																	borderColor: 'divider',
																	'&:last-child': { borderBottom: 0 },
																	'&:hover': { bgcolor: 'action.hover' },
																}}>
																<Box
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		justifyContent: 'space-between',
																		mb: 0.5,
																	}}>
																	<Chip
																		color={
																			notification.type === 'connected'
																				? 'success'
																				: notification.type === 'error'
																				? 'error'
																				: 'default'
																		}
																		label={notification.type}
																		size="small"
																		sx={{ textTransform: 'capitalize' }}
																	/>
																	<Typography color="text.secondary" variant="caption">
																		{notification.receivedAt
																			? new Date(notification.receivedAt).toLocaleTimeString()
																			: ''}
																	</Typography>
																</Box>
																{notification.payload?.message && (
																	<Typography
																		variant="body2"
																		sx={{
																			wordBreak: 'break-word',
																			mt: 0.5,
																		}}>
																		{String(notification.payload.message)}
																	</Typography>
																)}
																{notification.transactionId && (
																	<Typography
																		color="text.secondary"
																		variant="caption"
																		sx={{ display: 'block', mt: 0.5 }}>
																		ID: {notification.transactionId}
																	</Typography>
																)}
															</Box>
														))
													)}
												</Box>
											</Box>
										</Popover>
									</div>
								</Grid>
							)}
							<Grid item xs="auto">
								<div>
									<IconButton color="white" onClick={handleOpenNotifications}>
										<IconNotifications />
									</IconButton>

									<Popover
										anchorEl={anchorElNotifications}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'center',
										}}
										onClose={handleCloseNotifications}
										open={Boolean(anchorElNotifications)}>
										<Box sx={{ maxHeight: 300, maxWidth: 300 }}>
											<Notifications items={activityItems} />
										</Box>
									</Popover>
								</div>
							</Grid>

							<Grid item xs="auto">
								<Grid alignItems="center" container columnSpacing={1}>
									<Grid item xs="auto">
										<Box>
											<Grid alignItems="center" container spacing={1}>
												<Grid item>
													<Box
														component="button"
														onClick={handleOpenAccount}
														sx={{ display: 'flex' }}>
														<Avatar src={user?.photo} />
													</Box>
												</Grid>
												<Grid
													item
													sx={{ display: { xs: 'none !important', md: 'inline-flex !important' } }}>
													{user?.firstName && (
														<Button
															color="white"
															disableRipple={true}
															onClick={handleOpenAccount}
															sx={{ '&:hover': { backgroundColor: 'transparent' } }}
															variant="text">
															{user?.firstName}
														</Button>
													)}
												</Grid>
											</Grid>
										</Box>
										<Popover
											anchorEl={anchorElAccount}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'left',
											}}
											onClose={handleCloseAccount}
											open={Boolean(anchorElAccount)}>
											<Box sx={{ width: 260 }}>
												<Box sx={{ padding: 3 }}>
													<Grid
														alignItems="center"
														container
														flexDirection="column"
														justifyItems="center"
														spacing={1}>
														<Grid item>
															<Avatar className="large" src={user?.photo} />
														</Grid>
														<Grid
															item
															sx={{
																maxWidth: '100%',
																textAlign: 'center',
																wordBreak: 'break-word',
															}}>
															<Typography variant="subtitle2">{user?.fullName}</Typography>
															{!userEntity && (
																<Typography
																	color="brand.grey.500"
																	component="div"
																	variant="caption">
																	World Bank Group
																</Typography>
															)}
														</Grid>
														{userEntity && (
															<Grid item sx={{ maxWidth: '100%' }}>
																<SelectUserEntity />
															</Grid>
														)}
														{userChipLabel && (
															<Grid item sx={{ maxWidth: '100%' }}>
																<Chip
																	label={userChipLabel}
																	size="small"
																	sx={{ display: 'inline-flex' }}
																	variant="twoTone"
																/>
															</Grid>
														)}
													</Grid>
												</Box>
												<MenuList disablePadding={true}>
													<MenuItem
														component="button"
														onClick={handleLogout}
														sx={{
															width: '100%',
															textAlign: 'left',
														}}>
														<ListItemText>{t('logout')}</ListItemText>
													</MenuItem>
												</MenuList>
											</Box>
										</Popover>
									</Grid>

									<Grid item xs="auto" sx={{ display: { lg: 'none' } }}>
										<IconButton color="white" onClick={onOpenNav}>
											<IconMenu />
										</IconButton>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Toolbar>
		</AppBar>
	);
};
